// Create a new combined component: unified_info_header.js
import React, { useState, useEffect } from 'react';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Divider
} from '@mui/material';
import portalSession from '../portal_services/portal_session';

const UnifiedInfoHeader = ({ onLogout }) => {
  const theme = useTheme();
  const [companyData, setCompanyData] = useState(null);
  const [metadataData, setMetadataData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API configuration
  const BASE_URL = portalSession.getBaseURL();
  const CUSTOMER_ID = portalSession.getCustomerId();
  const CUSTOMER_TOKEN = portalSession.getCustomerToken();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [companyResponse, metadataResponse] = await Promise.all([
          fetch(`${BASE_URL}/get_customer/${CUSTOMER_ID}`, {
            method: 'GET',
            headers: {
              'X-Customer-ID': CUSTOMER_ID,
              'X-Customer-Token': CUSTOMER_TOKEN,
              'Content-Type': 'application/json',
            },
          }),
          fetch(`${BASE_URL}/get_metadata`, {
            method: 'GET',
            headers: {
              'X-Customer-ID': CUSTOMER_ID,
              'X-Customer-Token': CUSTOMER_TOKEN,
              'Content-Type': 'application/json',
            },
          })
        ]);

        if (!companyResponse.ok || !metadataResponse.ok) {
          throw new Error('Failed to fetch data');
        }

        const [companyData, metadataData] = await Promise.all([
          companyResponse.json(),
          metadataResponse.json()
        ]);

        setCompanyData(companyData);
        setMetadataData(metadataData);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCompanyName = (name) => {
    if (!name) return 'N/A';
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (err) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress color="primary" />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">Error loading data: {error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 3 }}>
      <Card 
        elevation={3}
        sx={{ 
          backgroundColor: 'white',
          border: '2px solid #009688',
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              color: '#009688',
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 3
            }}
          >
            {formatCompanyName(companyData?.company_name) || 'Customer Portal'}
          </Typography>
          
          <Grid container spacing={3}>
            {/* Company Information */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#009688',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  Company Details
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Company ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {companyData?.company_ID || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Content Information */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#009688',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  Content Status
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {formatDate(metadataData?.updated)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Page Count
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {metadataData?.previous_json_page_count || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Quick Stats */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#009688',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  System Status
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f0f9ff',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #e0f2f1',
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 'bold',
                    color: '#4CAF50'
                  }}>
                    Active
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UnifiedInfoHeader;